import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';

import { HostConfigProvider } from '@/host-config/presentation/HostConfigProvider';
import { FederationRootErrorBoundary } from '@/common/presentation/components/FederationRootErrorBoundary';
import CrashTestRoute from '@/common/main/routes/CrashTestRoute';
import BlockLibraryPage from '@/blocks/presentation/pages/BlockLibraryPage';
import { usePageFocusOnRouteChange } from '@/common/presentation/hooks/usePageFocusOnRouteChange';

const BlockEditorPage = lazy(() => import('@/blocks/presentation/pages/BlockEditorPage'));
const TemplateEditorPage = lazy(() => import('@/templates/presentation/pages/TemplateEditorPage'));
const TemplatesPage = lazy(() => import('@/templates/presentation/pages/TemplatesPage'));

/**
 * Federated entry point for the raptor_document_builder remote.
 *
 * a11y contract: this component is rendered INSIDE a host-provided `<main>`
 * landmark (when embedded) OR inside `StandaloneShell`'s `<main id="main-content">`
 * (when running standalone). It MUST NOT render its own `<main>`, skip-to-content
 * link, or primary navigation landmark — those belong to whichever shell mounts it.
 */
export default function RemoteRoutes() {
  usePageFocusOnRouteChange();

  return (
    <FederationRootErrorBoundary scope="RemoteRoutes" context={{ section: 'routes' }}>
      <HostConfigProvider>
        <NuqsAdapter>
          <Suspense fallback={null}>
            <Routes>
              <Route index element={<Navigate to="blocks" replace />} />
              <Route path="blocks">
                <Route index element={<BlockLibraryPage />} />
                <Route path=":blockId" element={<BlockEditorPage />} />
              </Route>
              <Route path="templates">
                <Route index element={<TemplatesPage />} />
                <Route path=":templateId" element={<TemplateEditorPage />} />
              </Route>
              {(import.meta.env.DEV || import.meta.env.VITE_E2E_OBSERVABILITY === 'true') && (
                <Route path="__observability/crash-test" element={<CrashTestRoute />} />
              )}
            </Routes>
          </Suspense>
        </NuqsAdapter>
      </HostConfigProvider>
    </FederationRootErrorBoundary>
  );
}
